import {
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { ImagePlus, CheckCircle2, Archive } from 'lucide-react';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { ProviderCauseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseStatus.schema';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

type CausesManagementListProps = {
  causes: RouterOutputs['groupRewards']['listCausesWithInfiniteScroll']['items'];
  onApproveCause: (causeId: string) => void;
  onArchiveCause: (causeId: string) => void;
  onEditCause: (causeId: string) => void;
  approvingCauseId?: string;
  archivingCauseId?: string;
};

const CausesManagementRow: React.FC<{
  cause: NonNullable<CausesManagementListProps['causes'][number]>;
  onApproveCause: (causeId: string) => void;
  onArchiveCause: (causeId: string) => void;
  onEditCause: (causeId: string) => void;
  approvingCauseId?: string;
  archivingCauseId?: string;
}> = React.memo(
  ({ cause, onApproveCause, onArchiveCause, onEditCause, approvingCauseId, archivingCauseId }) => {
    const handleApprove = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onApproveCause(cause.id);
      },
      [onApproveCause, cause.id],
    );

    const handleArchive = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onArchiveCause(cause.id);
      },
      [onArchiveCause, cause.id],
    );

    const handleEdit = useCallback(() => {
      onEditCause(cause.id);
    }, [onEditCause, cause.id]);

    const isApproving = approvingCauseId === cause.id;
    const isArchiving = archivingCauseId === cause.id;

    return (
      <Tr _hover={{ bg: 'bg.subtle' }} cursor="pointer" onClick={handleEdit}>
        <Td>
          <HStack spacing={2}>
            <Flex
              w="100px"
              aspectRatio={3 / 2}
              borderRadius="md"
              bg="bg.muted"
              align="center"
              justify="center"
              flexShrink={0}
              color="white"
            >
              {cause.imageUrl != null ? (
                <Image
                  src={cause.imageUrl}
                  alt={cause.name}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  borderRadius="md"
                />
              ) : (
                <Icon as={ImagePlus} boxSize={6} color="fg.muted" />
              )}
            </Flex>
            <Text fontWeight="medium">
              {cause.name}
              <Text as="span" ml={2} color="fg.muted" fontWeight="normal">
                Target: ${cause.targetAmount}
              </Text>
            </Text>
          </HStack>
        </Td>
        <Td>
          <HStack spacing={4}>
            <UserAvatar
              user={{
                first_name:
                  'suggestedBy' in cause && cause.suggestedBy?.first_name
                    ? cause.suggestedBy.first_name
                    : '',
                last_name:
                  'suggestedBy' in cause && cause.suggestedBy?.last_name != null
                    ? cause.suggestedBy.last_name
                    : '',
                picture_url:
                  'suggestedBy' in cause && cause.suggestedBy?.picture_url != null
                    ? cause.suggestedBy.picture_url
                    : null,
              }}
              size="sm"
            />
            <Text fontSize="sm" fontWeight="semibold">
              {'suggestedBy' in cause && cause.suggestedBy != null
                ? [cause.suggestedBy.first_name ?? '', cause.suggestedBy.last_name ?? '']
                    .filter(Boolean)
                    .join(' ')
                : ''}
            </Text>
          </HStack>
        </Td>
        <Td>
          <HStack spacing={2} justify="flex-end">
            <Button
              leftIcon={<Icon as={CheckCircle2} />}
              size="xs"
              isDisabled={
                cause.status === ProviderCauseStatusSchema.enum.APPROVED ||
                isApproving ||
                isArchiving
              }
              isLoading={isApproving}
              loadingText="Approving..."
              onClick={handleApprove}
            >
              {ProviderCauseStatusSchema.enum.APPROVED === cause.status ? (
                <Trans>Approved</Trans>
              ) : (
                <Trans>Approve</Trans>
              )}
            </Button>
            <Button
              leftIcon={<Icon as={Archive} />}
              variant="outline"
              size="xs"
              colorScheme="red"
              isDisabled={
                cause.status === ProviderCauseStatusSchema.enum.ARCHIVED ||
                isApproving ||
                isArchiving
              }
              isLoading={isArchiving}
              loadingText="Archiving..."
              onClick={handleArchive}
            >
              {ProviderCauseStatusSchema.enum.ARCHIVED === cause.status ? (
                <Trans>Archived</Trans>
              ) : (
                <Trans>Archive</Trans>
              )}
            </Button>
          </HStack>
        </Td>
      </Tr>
    );
  },
);

CausesManagementRow.displayName = 'CausesManagementRow';

export function CausesManagementList({
  causes,
  onApproveCause,
  onArchiveCause,
  onEditCause,
  approvingCauseId,
  archivingCauseId,
}: CausesManagementListProps) {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <Trans>Cause</Trans>
          </Th>
          <Th>
            <Trans>Suggested by</Trans>
          </Th>
          <Th textAlign="right">
            <Trans>Approve</Trans>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(causes) && causes.length
          ? causes.map((cause) => (
              <CausesManagementRow
                key={cause.id}
                cause={cause}
                onApproveCause={onApproveCause}
                onArchiveCause={onArchiveCause}
                onEditCause={onEditCause}
                approvingCauseId={approvingCauseId}
                archivingCauseId={archivingCauseId}
              />
            ))
          : null}
      </Tbody>
    </Table>
  );
}
